/*eslint complexity: ["error", 22]*/
/*eslint max-lines-per-function: ["error", 300]*/
import React, { useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';

import FormRow from '../../../app/components/FormRow';
import {
  CreateUpdateConsultationDto,
  CreateUpdateConsultationDtoTypeEnum,
  CreateUpdateConsultationDtoPdfTemplateTypeEnum,
  GetExamDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  ConsultationType,
  TemplateType,
} from '../../../common/spdCore/validation/schemas';
import { FormControlTextarea3 } from '../../../app/components/FormControlTextarea';
import { Role } from '../../../common/spdCore/authorization/roles';
import { ChooseSigned } from '../Users/ChooseSigned';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import momencik from '../../../common/momencik';

interface ConsultationPsychiatricEditorProps {
  exam: GetExamDto;
  consultation: CreateUpdateConsultationDto;
  id: string | undefined;
}

export const ConsultationPsychiatricEditor: React.FC<
  ConsultationPsychiatricEditorProps
> = props => {
  const spd = useSpdCore();
  const [consultation, setConsultation] = useState<CreateUpdateConsultationDto>(
    props.consultation,
  );
  const [id, setId] = useState<string | undefined>(props.id);
  const [message, setMessage] = useState<string>('');
  const [loadingSave, setLoadingSave] = useState<boolean>(false);

  const createConsultation = async (
    gConsultation: CreateUpdateConsultationDto,
  ) => {
    setMessage('');
    setLoadingSave(true);
    try {
      if (id) {
        await spd.consultations.update(id, gConsultation);
        setConsultation(gConsultation);

        await spd.pdfPrinter.saveAndPrintConsultation(
          id,
          TemplateType.Psychiatric_consultation,
        );
        setLoadingSave(false);
      } else {
        await spd.consultations
          .create({
            ...gConsultation,
            ...{
              pdfTemplateType:
                TemplateType.Psychiatric_consultation as unknown as CreateUpdateConsultationDtoPdfTemplateTypeEnum,
              examId: props?.exam?.id,
              type: ConsultationType.Psychiatric as unknown as CreateUpdateConsultationDtoTypeEnum,
            },
          })
          .then(async o => {
            setConsultation({
              ...o,
              ...{
                ...gConsultation,
                ...{
                  pdfTemplateType:
                    TemplateType.Psychiatric_consultation as unknown as CreateUpdateConsultationDtoPdfTemplateTypeEnum,
                  examId: props?.exam?.id,
                  type: ConsultationType.Psychiatric as unknown as CreateUpdateConsultationDtoTypeEnum,
                },
              },
            });
            setId(o.id);
            await spd.pdfPrinter.saveAndPrintConsultation(
              o.id,
              TemplateType.Psychiatric_consultation,
            );
            setLoadingSave(false);
          })
          .catch(err => {
            setLoadingSave(false);
            void responseErrors(err).then(errors => {
              setMessage(
                `Wystąpił problem z zapisem konsultacji psychiatrycznej:\n ${errors}`,
              );
            });
          });
      }
    } catch (response) {
      setLoadingSave(false);
      const error = await responseErrors(response as ResponseError);
      setMessage(
        `Błąd podczas zapisywania konsultacji psychiatrycznej:\n ${error}`,
      );
    }
  };

  const printConsultation = async () => {
    setMessage('');
    setLoadingSave(true);
    try {
      if (id) {
        await spd.pdfPrinter.loadAndPrintConsultation(id);
      }
    } catch (response) {
      const error = await responseErrors(response as ResponseError);
      setMessage(`Błąd podczas podglądu wydruku konsultacji:\n ${error}`);
    } finally {
      setLoadingSave(false);
    }
  };

  const propertyChange = (obj: Partial<CreateUpdateConsultationDto>) =>
    setConsultation({ ...consultation, ...obj });

  return (
    <Card className="mb-2">
      <Card.Header>{`Konsultacja psychiatryczna ${props.exam?.number}`}</Card.Header>
      <Card.Body>
        {props.exam.psychiatrist && !props.exam.psychiatristEnded && (
          <Alert variant="warning" className="mt-3">
            Po zakończeniu pracy z konsultacją psychiatryczną należy kliknąć
            przycisk "Zakończ badanie psychiatryczne" znajdujący się na tej
            stronie.
          </Alert>
        )}
        <Form className="d-grid gap-3">
          <div className="d-grid gap-3">
            <FormRow controlId="pass" label="Wynik pozytywny">
              <Form.Check
                type="checkbox"
                defaultChecked={
                  consultation?.pass ||
                  consultation?.pass === undefined ||
                  consultation?.pass === null
                    ? true
                    : false
                }
                onChange={e => propertyChange({ pass: e.target.checked })}
              />
            </FormRow>
            <FormRow controlId="staff" label="Lekarz">
              <ChooseSigned
                userUID={consultation?.authorUID}
                handleOnChange={user =>
                  setConsultation({
                    ...consultation,
                    authorUID: user?.uid,
                  })
                }
                roles={[Role.Psychiatrist]}
              />
            </FormRow>
            <FormRow controlId="number" label="Numer">
              <Form.Control
                type="text"
                defaultValue={consultation?.number || ''}
                onChange={e =>
                  propertyChange({
                    number: e.target.value,
                  })
                }
              />
            </FormRow>
            <FormRow controlId="consultDate" label="Data konsultacji">
              <Form.Control
                type="date"
                name="datepic"
                required={false}
                placeholder="DateRange"
                value={momencik(consultation?.consultDate, 'YYYY-MM-DD')}
                onChange={e =>
                  propertyChange({
                    consultDate: e.target.value
                      ? new Date(e.target.value)
                      : null,
                  })
                }
              />
            </FormRow>
            <FormRow controlId="descriptionExam" label="Rodzaj pozwolenia">
              <FormControlTextarea3
                defaultValue={consultation?.descriptionExam || ''}
                onChange={e =>
                  propertyChange({ descriptionExam: e.target.value })
                }
              />
            </FormRow>
            <FormRow controlId="interview" label="Wynik badania">
              <FormControlTextarea3
                defaultValue={consultation?.interview || ''}
                onChange={e => propertyChange({ interview: e.target.value })}
              />
            </FormRow>
            <FormRow controlId="recognition" label="Wynik konsultacji">
              <FormControlTextarea3
                defaultValue={consultation?.recognition || ''}
                onChange={e => propertyChange({ recognition: e.target.value })}
              />
            </FormRow>
          </div>
          {loadingSave && (
            <Alert variant="warning">
              <Spinner size="sm" className="mx-2" />
              Trwa generowanie konsultacji. Proszę nie zamykać tej strony.
            </Alert>
          )}
          <Button
            variant="outline-primary"
            className="ms-1"
            onClick={() => createConsultation(consultation)}
            disabled={loadingSave}
          >
            Zapisz i wydrukuj
          </Button>
          <Button
            variant="outline-primary"
            className="ms-1"
            onClick={() => printConsultation()}
            disabled={loadingSave}
          >
            Pokaż wydruk
          </Button>
          {message && <Alert variant={'warning'}>{message}</Alert>}
        </Form>
      </Card.Body>
    </Card>
  );
};
